// Customer-scoring engine for the Simulator.
// Explicit feature weights so the model is transparent — every value the
// user enters maps to a contribution that's visible on screen and adds
// up to the composite risk score.

export interface SelectOption {
  label: string;
  raw: number;       // raw points contributed by this option
}

export interface NumericFeature {
  key: string;
  label: string;
  type: "number";
  weight: number;       // multiplier applied to bucketed raw points
  unit?: string;
  defaultValue: number;
  min: number;
  max: number;
  step?: number;
  // map raw input → raw points (bucketed scale, ~0–10 typical)
  toRaw: (v: number) => number;
}

export interface SelectFeature {
  key: string;
  label: string;
  type: "select";
  weight: number;
  options: SelectOption[];
  defaultIdx: number;
}

export type Feature = SelectFeature | NumericFeature;

// =============== feature catalog ===============
// raw values are roughly on a 0–20 scale; weights cluster around 0.5–1.5.
// Final contribution = raw × weight. Sum capped at 100.
export const FEATURES: Feature[] = [
  {
    key: "tier",
    label: "Customer tier",
    type: "select",
    weight: 1.6,
    defaultIdx: 0,
    options: [
      { label: "Direct-to-owner",        raw: 0  },
      { label: "First-tier sub (under GC)", raw: 8  },
      { label: "Sub-to-a-sub",           raw: 18 },
    ],
  },
  {
    key: "ownerType",
    label: "Owner type",
    type: "select",
    weight: 1.0,
    defaultIdx: 0,
    options: [
      { label: "Federal",                  raw: -4 },
      { label: "Public REIT / utility",    raw: -2 },
      { label: "Institutional (univ./hosp.)", raw: 0 },
      { label: "Private corporate",        raw: 3  },
      { label: "Distressed / SPV",         raw: 12 },
    ],
  },
  {
    key: "naics",
    label: "Industry NAICS",
    type: "select",
    weight: 0.8,
    defaultIdx: 1,
    options: [
      { label: "325412 — Pharma / biotech",   raw: -1 },
      { label: "334413 — Semi / FAB",         raw: 0  },
      { label: "517311 — Telecom / data ctr", raw: 1  },
      { label: "622110 — Hospitals",          raw: 2  },
      { label: "611310 — Higher-ed",          raw: 2  },
      { label: "236220 — Commercial bldg",    raw: 3  },
      { label: "237110 — Civil / water",      raw: 5  },
    ],
  },
  {
    key: "geography",
    label: "Geography",
    type: "select",
    weight: 0.5,
    defaultIdx: 0,
    options: [
      { label: "RTP / Triangle, NC", raw: 0 },
      { label: "Boston Metro",       raw: 0 },
      { label: "DFW",                raw: 1 },
      { label: "Phoenix",            raw: 2 },
      { label: "NYC Metro",          raw: 3 },
      { label: "SF Bay",             raw: 3 },
    ],
  },
  {
    key: "contractType",
    label: "Contract type",
    type: "select",
    weight: 1.0,
    defaultIdx: 0,
    options: [
      { label: "GMP",                  raw: -3 },
      { label: "Lump-sum",             raw: 0  },
      { label: "Cost-plus fixed fee",  raw: 1  },
      { label: "Unit-price",           raw: 3  },
      { label: "T&M",                  raw: 5  },
    ],
  },
  {
    key: "archetype",
    label: "Project archetype",
    type: "select",
    weight: 0.7,
    defaultIdx: 0,
    options: [
      { label: "Clean Room",           raw: 0 },
      { label: "Commercial Interiors", raw: 1 },
      { label: "MEP",                  raw: 2 },
      { label: "Scaffold",             raw: 3 },
      { label: "Insulation",           raw: 4 },
    ],
  },
  {
    key: "payIfPaid",
    label: "Pay-if-paid clause",
    type: "select",
    weight: 1.4,
    defaultIdx: 0,
    options: [
      { label: "No",  raw: 0 },
      { label: "Yes", raw: 8 },
    ],
  },
  {
    key: "contractValue",
    label: "Expected contract value",
    type: "number",
    weight: 0.5,
    unit: "$M",
    defaultValue: 12.5,
    min: 0.5,
    max: 250,
    step: 0.5,
    toRaw: (v) => v <= 5 ? 0 : v <= 25 ? 2 : v <= 100 ? 4 : 7,
  },
  {
    key: "paymentTerms",
    label: "Payment terms",
    type: "number",
    weight: 0.9,
    unit: "days",
    defaultValue: 45,
    min: 15,
    max: 120,
    step: 5,
    toRaw: (v) => v <= 30 ? 0 : v <= 45 ? 2 : v <= 60 ? 4 : v <= 75 ? 6 : 9,
  },
  {
    key: "retainage",
    label: "Retainage held by customer",
    type: "number",
    weight: 0.6,
    unit: "%",
    defaultValue: 10,
    min: 0,
    max: 25,
    step: 1,
    toRaw: (v) => v <= 5 ? 0 : v <= 10 ? 2 : v <= 15 ? 4 : 7,
  },
  {
    key: "paydex",
    label: "D&B Paydex",
    type: "number",
    weight: 1.5,
    defaultValue: 75,
    min: 1,
    max: 100,
    step: 1,
    toRaw: (v) => v >= 80 ? -3 : v >= 70 ? 0 : v >= 60 ? 5 : v >= 50 ? 10 : 18,
  },
];

export type FeatureValues = Record<string, number>;

export function defaultValues(): FeatureValues {
  const v: FeatureValues = {};
  FEATURES.forEach((f) => {
    if (f.type === "select") v[f.key] = f.defaultIdx;
    else v[f.key] = f.defaultValue;
  });
  return v;
}

export interface Contribution {
  key: string;
  feature: string;          // human label
  selectedValue: string;    // displayed value chosen
  raw: number;
  weight: number;
  contribution: number;     // raw × weight
}

export function computeContributions(values: FeatureValues): Contribution[] {
  return FEATURES.map((f) => {
    if (f.type === "select") {
      const idx = Math.max(0, Math.min(f.options.length - 1, values[f.key] | 0));
      const opt = f.options[idx];
      return {
        key: f.key,
        feature: f.label,
        selectedValue: opt.label,
        raw: opt.raw,
        weight: f.weight,
        contribution: opt.raw * f.weight,
      };
    } else {
      const v = values[f.key];
      const raw = f.toRaw(v);
      return {
        key: f.key,
        feature: f.label,
        selectedValue: `${v}${f.unit ? " " + f.unit : ""}`,
        raw,
        weight: f.weight,
        contribution: raw * f.weight,
      };
    }
  });
}

export type RiskBand = "critical" | "high" | "medium" | "low";

export interface RiskAssessment {
  score: number;            // 0..100
  band: RiskBand;
  bandLabel: string;
}

export function assess(contributions: Contribution[]): RiskAssessment {
  const raw = contributions.reduce((a, c) => a + c.contribution, 0);
  // Normalize: contributions can theoretically span ~ -10 to +90.
  // Linear-clamp to 0..100. Anchor: 0 risk if sum ≤ 0; 100 if sum ≥ 90.
  const score = Math.max(0, Math.min(100, Math.round(raw)));
  const band: RiskBand =
    score >= 75 ? "critical" :
    score >= 50 ? "high" :
    score >= 25 ? "medium" :
    "low";
  const bandLabel =
    band === "critical" ? "CRITICAL" :
    band === "high" ? "HIGH" :
    band === "medium" ? "MEDIUM" : "LOW";
  return { score, band, bandLabel };
}

// Derived predictions (illustrative) — these would come from a calibrated
// model in production but their inputs are the same features above.
export interface DerivedKpis {
  predictedDsoDays: number;
  predictedDsoLow: number;
  predictedDsoHigh: number;
  pDefault12m: number;            // 0..1
  expectedOverUnder: number;      // $ amount
  cashConversionMonths: number;   // months
}

export function deriveKpis(values: FeatureValues): DerivedKpis {
  // Tier maps to base DSO
  const tierIdx = values.tier | 0;
  const baseDso = tierIdx === 0 ? 50 : tierIdx === 1 ? 65 : 95;
  const paydex = values.paydex;
  const terms = values.paymentTerms;
  const payIfPaidYes = (values.payIfPaid | 0) === 1;

  const paydexAdj = paydex >= 80 ? -4 : paydex >= 70 ? 0 : paydex >= 60 ? 6 : paydex >= 50 ? 14 : 22;
  const termsAdj = (terms - 45) * 0.4;
  const ppAdj = payIfPaidYes ? 8 : 0;
  const predictedDsoDays = Math.max(15, Math.round(baseDso + paydexAdj + termsAdj + ppAdj));
  const predictedDsoLow = Math.max(10, predictedDsoDays - 8);
  const predictedDsoHigh = predictedDsoDays + 12;

  const contributions = computeContributions(values);
  const { score } = assess(contributions);
  const pDefault12m = Math.max(0, Math.min(0.5, score / 220));

  const cv = values.contractValue * 1e6;
  const expectedOverUnder = -1 * (predictedDsoDays / 365) * cv * 0.18 * (score / 100);
  const cashConversionMonths = Math.max(0.5, predictedDsoDays / 30);

  return {
    predictedDsoDays,
    predictedDsoLow,
    predictedDsoHigh,
    pDefault12m,
    expectedOverUnder,
    cashConversionMonths,
  };
}
